/**
 * How a lead time reads on a chip: "15 min", "1 hour", "3 days", "On the day".
 *
 * Zero means different things by unit — for a clock-out reminder it is the planned end
 * itself, for an equipment return it is the morning of the day — so it gets its own
 * words rather than "0 min".
 */

import type { Locale, TranslationKey, TranslationValues } from '@/i18n';
import { intlTag } from '@/lib/format';

type T = (key: TranslationKey, values?: TranslationValues) => string;

export function leadLabel(t: T, value: number, unit: 'minutes' | 'days'): string {
  if (unit === 'days') {
    if (value === 0) return t('notifications.sameDay');
    return value === 1 ? t('notifications.dayOne') : t('notifications.days', { count: value });
  }
  if (value === 0) return t('notifications.atEnd');
  if (value % 1440 === 0) {
    const days = value / 1440;
    return days === 1 ? t('notifications.dayOne') : t('notifications.days', { count: days });
  }
  if (value % 60 === 0) {
    const hours = value / 60;
    return hours === 1 ? t('notifications.hourOne') : t('notifications.hours', { count: hours });
  }
  return t('notifications.minutes', { count: value });
}

/** "3 minutes ago", "yesterday" — in the reader's language. */
export function relativeTime(iso: string, locale: string, now = Date.now()): string {
  const seconds = Math.round((Date.parse(iso) - now) / 1000);
  const tag = intlTag(locale as Locale);
  const rtf = new Intl.RelativeTimeFormat(tag, { numeric: 'auto' });
  const abs = Math.abs(seconds);
  if (abs < 60) return rtf.format(0, 'minute');
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(seconds / 3600), 'hour');
  if (abs < 7 * 86_400) return rtf.format(Math.round(seconds / 86_400), 'day');
  return new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short' }).format(new Date(iso));
}

/**
 * Links written for the app (`/clock`, `/my-equipment`) have no page in the portal.
 * Only these open something here; the rest just mark the notification read.
 */
const PORTAL_PREFIXES = ['/jobs/', '/shifts/', '/messages', '/compliance', '/inventory', '/documents', '/calendar', '/leave'];

export function portalLink(link: string | null): string | null {
  if (!link) return null;
  return PORTAL_PREFIXES.some((p) => link === p || link.startsWith(p)) ? link : null;
}
